import React, { useState, useRef } from 'react';
import { Box, Button, Grid2, Typography, Accordion, AccordionSummary, AccordionDetails, CircularProgress } from '@mui/material';
import ExpandMoreIcon from '@mui/icons-material/ExpandMore';
import { useGetLmsGrades, useGetLmsSubject, useGetAiLms, useGetVersionContent } from '../../assets/nodeApi/getApi/getAdminApi';
import { Form, Formik, Field } from 'formik';
import { TextFormField } from '../../shared/components/formfields/TextFormField';
import { AutoCompleteField } from '../../shared/components/formfields/AutoCompleteField';
import { Icon } from '@iconify/react';

const sharedContentStyles = {
    wordBreak: 'break-word',
    overflowWrap: 'break-word',
    '& h1, & h2, & h3, & h4, & h5, & h6': {
        mt: 2,
        mb: 1,
        fontWeight: 600,
        color: '#111827',
    },
    '& h1': { fontSize: '24px' },
    '& h2': { fontSize: '20px' },
    '& h3': { fontSize: '18px' },
    '& p': {
        mb: 2,
        fontSize: '14px',
        lineHeight: 1.6,
        color: '#374151',
    },
    '& ul, & ol': {
        mb: 2,
        pl: 3,
        color: '#374151',
    },
    '& li': {
        mb: 0.5,
        fontSize: '14px',
        lineHeight: 1.6,
    },
    '& strong': {
        fontWeight: 600,
        color: '#111827',
    },
    '& em': {
        fontStyle: 'italic',
    },
    '& blockquote': {
        borderLeft: '4px solid #E5E7EB',
        pl: 2,
        py: 0.5,
        my: 2,
        color: '#6B7280',
        fontStyle: 'italic',
        backgroundColor: '#F9FAFB'
    },
    '& table': {
        width: '100%',
        borderCollapse: 'collapse',
        mb: 2,
        display: 'block',
        overflowX: 'auto',
    },
    '& th, & td': {
        border: '1px solid #E5E7EB',
        p: 1,
        textAlign: 'left',
        fontSize: '14px',
    },
    '& th': {
        backgroundColor: '#F9FAFB',
        fontWeight: 600,
    },
    '& pre': {
        background: '#F9FAFB',
        p: 2,
        borderRadius: '4px',
        overflowX: 'auto',
        whiteSpace: 'pre-wrap',
        wordBreak: 'break-word',
        fontSize: '13px',
        color: '#374151',
    },
    '& code': {
        background: '#F9FAFB',
        px: 0.5,
        py: 0.2,
        borderRadius: '4px',
        fontSize: '13px',
        color: '#d63384',
        wordBreak: 'break-word',
    },
    '& img': {
        maxWidth: '100%',
        height: 'auto',
        display: 'block',
        margin: '16px auto',
        borderRadius: '8px',
    }
};

const VersionContent = ({ lmsId }) => {
    const { data: versionResponse, isLoading } = useGetVersionContent(lmsId);

    if (isLoading) return <Box sx={{ display: 'flex', justifyContent: 'center', p: 2 }}><CircularProgress size={24} /></Box>;

    const versions = versionResponse?.data?.versions || [];

    if (versions.length === 0) {
        return <Typography sx={{ color: '#6B7280', fontSize: '14px' }}>No versions found.</Typography>;
    }

    return (
        <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2, mt: 1 }}>
            {versions.map((ver) => {
                const isPublished = ver.is_final === true;

                return (
                    <Accordion key={ver._id} sx={{
                        border: isPublished ? '2px solid #22C55E' : '1px solid #E5E7EB',
                        background: isPublished ? '#F0FDF4' : '#fff',
                        borderRadius: '8px !important',
                        boxShadow: 'none',
                        '&:before': { display: 'none' }
                    }}>
                        <AccordionSummary expandIcon={<ExpandMoreIcon sx={{ color: isPublished ? '#15803D' : '#6B7280' }} />}>
                            <Typography sx={{ fontWeight: 500, color: isPublished ? '#15803D' : '#374151', fontSize: '16px' }}>
                                Version {ver.versionNumber} {isPublished && "(Published)"}
                            </Typography>
                        </AccordionSummary>
                        <AccordionDetails sx={{ borderTop: isPublished ? '1px solid #BBF7D0' : '1px solid #E5E7EB', pt: 3 }}>
                            <Box
                                dangerouslySetInnerHTML={{ __html: ver.generatedContent }}
                                sx={{ ...sharedContentStyles, background: 'transparent' }}
                            />
                        </AccordionDetails>
                    </Accordion>
                );
            })}
        </Box>
    );
};

const AccordionItem = ({ item }) => {
    const [expanded, setExpanded] = useState(false);

    const handleChange = () => {
        setExpanded(!expanded);
    };

    return (
        <Accordion expanded={expanded} onChange={handleChange} sx={{
            mb: 1.5,
            border: '1px solid #A5B4FC',
            borderRadius: '6px !important',
            boxShadow: 'none',
            '&:before': { display: 'none' }
        }}>
            <AccordionSummary expandIcon={<ExpandMoreIcon sx={{ color: '#6366F1' }} />} sx={{ background: '#F5F7FF', borderRadius: expanded ? '6px 6px 0 0' : '6px' }}>
                <Typography sx={{ fontSize: '14px', color: '#374151' }}>
                    Student Understanding Level - {item.student_level}
                </Typography>
            </AccordionSummary>
            <AccordionDetails sx={{ background: '#fff', borderTop: '1px solid #E5E7EB', borderRadius: '0 0 6px 6px', p: 3 }}>
                {expanded && <VersionContent lmsId={item._id} />}
            </AccordionDetails>
        </Accordion>
    );
};

const AILmsList = ({ items }) => {
    const grouped = items.reduce((acc, item) => {
        const key = item.teacher_efficacy || 'Unknown';
        if (!acc[key]) acc[key] = [];
        acc[key].push(item);
        return acc;
    }, {});

    return (
        <Box>
            {Object.entries(grouped).map(([efficacy, groupItems]) => (
                <Box key={efficacy} sx={{ mb: 4 }}>
                    <Typography sx={{ fontSize: '14px', color: '#4169E1', fontWeight: 500, mb: 2 }}>
                        Teacher Efficacy - {efficacy}
                    </Typography>
                    {groupItems.map(item => (
                        <AccordionItem key={item._id} item={item} />
                    ))}
                </Box>
            ))}
            {items.length === 0 && (
                <Typography sx={{ color: '#6B7280', textAlign: 'center', py: 4 }}>
                    No records found.
                </Typography>
            )}
        </Box>
    );
};

const ViewAiLms = () => {
    const [filterParams, setFilterParams] = useState({
        topic: "",
        subject: "",
        grade: ""
    });
    const [isFilterApplied, setIsFilterApplied] = useState(false);
    const formikRef = useRef();

    const { data: subjects, isLoading: isSubjectsLoading } = useGetLmsSubject();
    const { data: grades, isLoading: isGradesLoading } = useGetLmsGrades();

    const subjectOptions = subjects?.data?.filter((item) => item.status === "active")?.map((item) => ({
        label: item.subject,
        value: String(item.subject),
        id: String(item.id),
    })) || [];

    const gradeOptions = grades?.data?.map((item) => ({
        label: item.name,
        value: String(item.numeric_value),
        id: String(item.numeric_value),
    })) || [];

    const initialValues = {
        gradeId: '',
        subjectId: '',
        topicId: '',
    };

    const handleFilter = (values) => {
        const selectedSubject = subjectOptions.find(opt => opt.value === String(values.subjectId) || opt.id === String(values.subjectId));
        const subjectIdNum = selectedSubject ? Number(selectedSubject.id) : values.subjectId ? Number(values.subjectId) : "";

        const selectedGrade = gradeOptions.find(opt => opt.value === String(values.gradeId) || opt.id === String(values.gradeId));
        const gradeIdNum = selectedGrade ? Number(selectedGrade.id) : values.gradeId ? Number(values.gradeId) : "";

        setFilterParams({
            topic: values.topicId || "",
            subject: subjectIdNum,
            grade: gradeIdNum
        });
        setIsFilterApplied(true);
    };

    const handleReset = () => {
        formikRef.current?.resetForm();
        setFilterParams({
            topic: "",
            subject: "",
            grade: ""
        });
        setIsFilterApplied(false);
    };

    const { data: aiLmsListResponse, isLoading: isListLoading } = useGetAiLms(filterParams, { enabled: isFilterApplied });
    const aiLmsList = aiLmsListResponse?.data?.contents || [];

    return (
        <Box sx={{
            fontFamily: "'Poppins', sans-serif",
            background: '#fff',
            borderRadius: '16px',
            border: '1px solid #F3F4F6',
            p: { xs: '20px', md: '32px' },
            mx: 'auto',
            maxWidth: '1200px'
        }}>
            <Box sx={{ mb: 4 }}>
                <Typography sx={{ fontSize: '22px', fontWeight: 500, color: '#111827', mb: '4px' }}>
                    VIEW AI LMS
                </Typography>
                <Typography sx={{ fontSize: '14px', color: '#4B5563', mb: '32px' }}>
                    Create and organize syllabus details for better course planning
                </Typography>
            </Box>

            <Formik
                innerRef={formikRef}
                initialValues={initialValues}
                onSubmit={handleFilter}
            >
                {({ submitForm, values }) => {
                    const isFilterEmpty = !values.gradeId && !values.subjectId && !values.topicId;
                    return (
                    <Form>
                        <Grid2 container spacing={4} sx={{ mb: 4 }}>
                            <Grid2 size={{ xs: 12, md: 6 }}>
                                <Field
                                    name="gradeId"
                                    component={AutoCompleteField}
                                    label="Grade"
                                    isRequired
                                    options={gradeOptions}
                                    loading={isGradesLoading}
                                />
                            </Grid2>
                            <Grid2 size={{ xs: 12, md: 6 }}>
                                <Field
                                    name="subjectId"
                                    component={AutoCompleteField}
                                    label="Subject"
                                    isRequired
                                    options={subjectOptions}
                                    loading={isSubjectsLoading}
                                />
                            </Grid2>
                            <Grid2 size={{ xs: 12, md: 6 }}>
                                <Field
                                    name="topicId"
                                    component={TextFormField}
                                    label="Topic"
                                    isRequired
                                    placeholder="Enter Topic"
                                />
                            </Grid2>
                        </Grid2>
                        <Box sx={{ display: 'flex', justifyContent: 'flex-end', gap: 2, mb: 4 }}>
                            <Button variant="outlined" onClick={handleReset} sx={{ textTransform: 'none', borderRadius: '6px', borderColor: '#D1D5DB', color: '#374151', px: 3 }}>Clear</Button>
                            <Button variant="contained" onClick={submitForm} disabled={isFilterEmpty} sx={{ background: '#4169E1', color: "#fff", textTransform: 'none', borderRadius: '6px', px: 3, boxShadow: 'none', '&:hover': { background: '#3154B5', boxShadow: 'none' }, '&.Mui-disabled': { background: '#E5E7EB', color: '#9CA3AF' } }}>Search</Button>
                        </Box>
                    </Form>
                )}}
            </Formik>

            {!isFilterApplied ? (
                <Box sx={{ display: 'flex', flexDirection: 'column', alignItems: 'center', justifyContent: 'center', py: 8 }}>
                    <Icon icon="ri:color-filter-ai-line" style={{ fontSize: '72px', color: '#babbbdff', marginBottom: '16px' }} />
                    <Typography sx={{ color: '#6B7280', fontSize: '16px', textAlign: 'center' }}>
                        Please select the topic, subject, or grade filter to view data.
                    </Typography>
                </Box>
            ) : (
                <>
                    <Typography sx={{ fontSize: '18px', fontWeight: 400, color: '#374151', mb: 3 }}>
                        List of created AI LMS
                    </Typography>

                    {isListLoading ? (
                        <Box sx={{ display: 'flex', justifyContent: 'center', py: 5 }}>
                            <CircularProgress />
                        </Box>
                    ) : (
                        <AILmsList items={aiLmsList} />
                    )}
                </>
            )}
        </Box>
    );
};

export default ViewAiLms;